import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Form,
  Modal,
  Popconfirm,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FormInstance } from 'antd/es/form';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { CardTitle } from '@/components/EntityId';
import { api } from '@/api/client';
import { useApiMutation } from '@/api/hooks';
import { createdAtColumn, newestFirst } from '@/components/createdAtColumn';
import { StatusFilter } from '@/components/StatusFilter';
import { StickyTable, type StickyColumn } from '@/components/StickyTable';

export interface CrudPanelProps<T extends { id: number; isActive?: boolean }> {
  /** Базовый путь ресурса, например `/dictionaries/fuel-types` */
  url: string;
  queryKey: string;
  title: string;
  /** Пояснение, зачем этот справочник и что будет при удалении */
  description?: ReactNode;
  columns: Array<StickyColumn<T>>;
  /**
   * Поля формы. Получает признак редактирования (часть полей неизменяема)
   * и саму редактируемую запись — например, чтобы не предлагать
   * подразделению в вышестоящие его же вложенные.
   */
  formFields: (isEdit: boolean, form: FormInstance, editing: T | null) => ReactNode;
  /** Значения формы из строки таблицы. По умолчанию — сама строка. */
  toFormValues?: (row: T) => Record<string, unknown>;
  canManage: boolean;
  /**
   * Право только добавлять, без правки и удаления чужих записей. По
   * умолчанию совпадает с canManage.
   */
  canCreate?: boolean;
  /** Дополнительные ключи кэша, которые надо сбросить после изменения */
  invalidateExtra?: string[][];
  /** Сначала новые. Отключается для справочников-классификаторов (регионы), где нужен алфавит. */
  newestFirst?: boolean;
  /**
   * Фильтр по статусу над таблицей. Без него показываются все записи —
   * и активные, и отключённые: статус и так виден в колонке, а скрывать
   * отключённые без способа их показать значило бы потерять к ним доступ.
   */
  statusFilter?: boolean;
  /**
   * Режим дерева: родитель записи. Строки вкладываются друг в друга,
   * по умолчанию всё раскрыто. Запись, чей родитель не попал в выборку
   * (например, отключён и скрыт фильтром), показывается на верхнем уровне,
   * чтобы не пропасть из списка.
   */
  treeParent?: (row: T) => number | null;
}

type TreeRow<T> = T & { children?: Array<TreeRow<T>> };

function buildTree<T extends { id: number }>(
  rows: T[],
  parentOf: (row: T) => number | null,
): Array<TreeRow<T>> {
  const nodes = new Map<number, TreeRow<T>>(rows.map((row) => [row.id, { ...row }]));
  const roots: Array<TreeRow<T>> = [];
  for (const row of rows) {
    const node = nodes.get(row.id)!;
    const parentId = parentOf(row);
    const parent = parentId !== null ? nodes.get(parentId) : undefined;
    if (parent) {
      parent.children = [...(parent.children ?? []), node];
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/**
 * Универсальная панель справочника.
 *
 * Абстракция оправдана здесь и только здесь: пять справочников отличаются
 * набором полей, но не поведением — список, создание, правка, отключение.
 * Писать это пять раз означало бы пять мест, где надо не забыть сбросить кэш
 * или обработать ошибку.
 */
export function CrudPanel<T extends { id: number; isActive?: boolean }>({
  url,
  queryKey,
  title,
  description,
  columns,
  formFields,
  toFormValues,
  canManage,
  canCreate = canManage,
  invalidateExtra = [],
  newestFirst: sortNewestFirst = true,
  statusFilter = true,
  treeParent,
}: CrudPanelProps<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  /*
    Фильтр по статусу. По умолчанию — «активен»: отключённые записи остаются
    в справочнике ради истории, но в повседневной работе не нужны. Так же вёл
    себя прежний флажок «Показывать отключённые»; фильтр добавляет к нему
    третий вариант — посмотреть только отключённые.
  */
  const [status, setStatus] = useState<Array<'active' | 'inactive'>>(['active']);
  /** В режиме дерева: раскрытые узлы. По умолчанию всё свёрнуто. */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [form] = Form.useForm();

  // Справочники невелики, поэтому список грузится целиком, а фильтр работает
  // на месте: переключение не ждёт сервера.
  const query = useQuery({
    queryKey: [queryKey],
    queryFn: async () => (await api.get<T[]>(url, { params: { includeInactive: true } })).data,
  });

  // Не у каждого справочника есть признак активности — тогда и фильтра нет.
  const hasStatus =
    statusFilter && (query.data ?? []).some((row) => row.isActive !== undefined);
  const visibleRows = (query.data ?? []).filter(
    (row) =>
      !hasStatus ||
      status.length === 0 ||
      status.includes(row.isActive !== false ? 'active' : 'inactive'),
  );

  const invalidate = [[queryKey], ['dictionaries'], ...invalidateExtra];

  /** В режиме дерева: порядковый номер записи среди соседей по ветке. */
  const siblingIndex = new Map<number, number>();
  if (treeParent) {
    const ids = new Set(visibleRows.map((row) => row.id));
    const counters = new Map<number | null, number>();
    for (const row of visibleRows) {
      const parentId = treeParent(row);
      const branch = parentId !== null && ids.has(parentId) ? parentId : null;
      const index = counters.get(branch) ?? 0;
      siblingIndex.set(row.id, index);
      counters.set(branch, index + 1);
    }
  }

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing) {
      form.setFieldsValue(toFormValues ? toFormValues(editing) : editing);
    }
  }, [open, editing, form, toFormValues]);

  const save = useApiMutation(
    async (values: Record<string, unknown>) => {
      if (editing) {
        return (await api.patch(`${url}/${editing.id}`, values)).data;
      }
      return (await api.post(url, values)).data;
    },
    { successMessage: editing ? 'Изменения сохранены' : 'Запись добавлена', invalidate },
  );

  const remove = useApiMutation(
    async (id: number) => (await api.delete(`${url}/${id}`)).data,
    { successMessage: t("Запись удалена"), invalidate },
  );

  // Не у всех справочников есть дата создания (регионы, виды топлива) — тогда колонки нет.
  const hasCreatedAt = visibleRows.some(
    (row) => (row as { createdAt?: string }).createdAt !== undefined,
  );
  const createdColumn: ColumnsType<T> = hasCreatedAt && sortNewestFirst ? [createdAtColumn<T>(t)] : [];

  const actionColumn: ColumnsType<T> = canManage
    ? [
        {
          title: '',
          width: 90,
          render: (_: unknown, row: T) => (
            <Space size={0}>
              <Tooltip title={t("Изменить")}>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditing(row);
                    setOpen(true);
                  }}
                />
              </Tooltip>
              <Popconfirm
                title={t("Удалить запись?")}
                description={t("Если на неё есть ссылки, запись будет отключена, а не удалена.")}
                okText={t("Удалить")}
                cancelText={t("Отмена")}
                onConfirm={() => remove.mutate(row.id)}
              >
                <Tooltip title={t("Удалить")}>
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          ),
        },
      ]
    : [];

  return (
    <>
      {description && (
        <Typography.Paragraph type="secondary">{description}</Typography.Paragraph>
      )}

      <Space style={{ marginBottom: 12 }} wrap>
        {canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            {t("Добавить")}
          </Button>
        )}
        {hasStatus && (
          <StatusFilter
            width={150}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'active', label: t('активен'), color: 'green' },
              { value: 'inactive', label: t('отключён'), color: 'default' },
            ]}
          />
        )}
      </Space>

      <StickyTable<T>
        rowKey="id"
        // В дереве номер строки сбивался бы у вложенных (отсчёт заново в каждой
        // ветке), а постраничный вывод резал бы ветки — оба отключены.
        rowNumbers={!treeParent}
        size="small"
        loading={query.isLoading}
        dataSource={
          treeParent
            ? buildTree(visibleRows, treeParent)
            : sortNewestFirst
              ? newestFirst(visibleRows as Array<T & { createdAt?: string | null }>)
              : visibleRows
        }
        pagination={treeParent ? false : { pageSize: 20, hideOnSinglePage: true }}
        expandable={
          treeParent
            ? {
                expandedRowKeys: [...expanded],
                onExpand: (isOpen, row) =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (isOpen) next.add(row.id);
                    else next.delete(row.id);
                    return next;
                  }),
              }
            : undefined
        }
        className={treeParent ? 'tms-tree-table' : undefined}
        // Отключённые записи приглушены: они остаются в списке ради истории,
        // но в формах уже не предлагаются.
        rowClassName={(row) =>
          [
            row.isActive === false ? 'row-inactive' : '',
            // Номер среди соседей по ветке — задержка появления «лесенкой»
            // (см. .tms-tree-table в styles.css). Больше 8 не нужно: длинная
            // ветка иначе проявлялась бы слишком долго.
            treeParent ? `tms-tree-i${Math.min(siblingIndex.get(row.id) ?? 0, 8)}` : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        columns={[...columns, ...createdColumn, ...actionColumn]}
      />

      <Modal
        open={open}
        title={<CardTitle title={editing ? `${title}: изменение` : `${title}: добавление`} id={editing?.id} />}
        okText={t("Сохранить")}
        cancelText={t("Отмена")}
        confirmLoading={save.isPending}
        onCancel={() => setOpen(false)}
        onOk={() => {
          void form.validateFields().then((values) => {
            save.mutate(values, { onSuccess: () => setOpen(false) });
          });
        }}
      >
        <Form form={form} layout="vertical">
          {formFields(Boolean(editing), form, editing)}
        </Form>
      </Modal>
    </>
  );
}
